/**
 * Helper para manejar abreviaturas y nombres completos de libros bíblicos
 * Versión: 1.0
 * Fecha: 10 de Noviembre, 2025
 */

// Mapeo completo de abreviaturas a nombres completos
export const LIBROS_BIBLIA = {
  // Antiguo Testamento - Pentateuco (Torah)
  'Gn': 'Génesis',
  'Ex': 'Éxodo',
  'Lv': 'Levítico',
  'Nm': 'Números',
  'Dt': 'Deuteronomio',
  
  // Libros Históricos
  'Jos': 'Josué',
  'Jue': 'Jueces',
  'Rt': 'Rut',
  '1 Sa': '1 Samuel',
  '2 Sa': '2 Samuel',
  '1 Re': '1 Reyes',
  '2 Re': '2 Reyes',
  '1 Cr': '1 Crónicas',
  '2 Cr': '2 Crónicas',
  '1Cr': '1 Crónicas',  // Variante sin espacio
  '2Cr': '2 Crónicas',  // Variante sin espacio
  'Esd': 'Esdras',
  'Neh': 'Nehemías',
  'Est': 'Ester',
  
  // Libros Poéticos y Sapienciales
  'Job': 'Job',
  'Sal': 'Salmos',
  'Pr': 'Proverbios',
  'Ec': 'Eclesiastés',
  'Cnt': 'Cantar de los Cantares',
  
  // Profetas Mayores
  'Is': 'Isaías',
  'Jer': 'Jeremías',
  'Lm': 'Lamentaciones',
  'Ez': 'Ezequiel',
  'Dn': 'Daniel',
  
  // Profetas Menores
  'Os': 'Oseas',
  'Jl': 'Joel',
  'Am': 'Amós',
  'Abd': 'Abdías',
  'Jon': 'Jonás',
  'Miq': 'Miqueas',
  'Nah': 'Nahúm',
  'Hab': 'Habacuc',
  'Sof': 'Sofonías',
  'Hag': 'Hageo',
  'Zac': 'Zacarías',
  'Mal': 'Malaquías',
  
  // Nuevo Testamento - Evangelios
  'Mt': 'Mateo',
  'Mc': 'Marcos',
  'Lc': 'Lucas',
  'Jn': 'Juan',
  
  // Hechos
  'Hch': 'Hechos',
  
  // Epístolas Paulinas
  'Ro': 'Romanos',
  '1 Co': '1 Corintios',
  '2 Co': '2 Corintios',
  'Gl': 'Gálatas',
  'Ef': 'Efesios',
  'Flp': 'Filipenses',
  'Col': 'Colosenses',
  '1 Ts': '1 Tesalonicenses',
  '2 Ts': '2 Tesalonicenses',
  '1 Ti': '1 Timoteo',
  '2 Ti': '2 Timoteo',
  'Ti': 'Tito',
  'Flm': 'Filemón',
  
  // Epístola a los Hebreos
  'Heb': 'Hebreos',
  
  // Epístolas Generales
  'Stg': 'Santiago',
  '1 Pe': '1 Pedro',
  '2 Pe': '2 Pedro',
  '1 Jn': '1 Juan',
  '2 Jn': '2 Juan',
  '3 Jn': '3 Juan',
  'Jud': 'Judas',
  
  // Apocalipsis
  'Ap': 'Apocalipsis',
  
  // Variantes alternativas comunes
  '1Co': '1 Corintios',
  '2Co': '2 Corintios',
  '1Ts': '1 Tesalonicenses',
  '2Ts': '2 Tesalonicenses',
  '1Ti': '1 Timoteo',
  '2Ti': '2 Timoteo',
  '1Pe': '1 Pedro',
  '2Pe': '2 Pedro',
  '1Jn': '1 Juan',
  '2Jn': '2 Juan',
  '3Jn': '3 Juan',
  '1Sa': '1 Samuel',
  '2Sa': '2 Samuel',
  '1Re': '1 Reyes',
  '2Re': '2 Reyes',
};

/**
 * Convierte una abreviatura de libro a su nombre completo
 * @param {string} abreviatura - La abreviatura del libro (ej: "Gn", "1 Co")
 * @returns {string} - El nombre completo del libro o la abreviatura si no se encuentra
 */
export function obtenerNombreCompleto(abreviatura) {
  if (!abreviatura) return '';
  
  // Limpiar espacios extras
  const abrevLimpia = abreviatura.trim();
  
  // Buscar en el mapeo
  return LIBROS_BIBLIA[abrevLimpia] || abrevLimpia;
}

/**
 * Convierte una referencia completa (libro + capítulos) expandiendo el nombre del libro
 * @param {string} referencia - La referencia completa (ej: "Gn 1-3", "1 Co 5:1-13")
 * @param {boolean} usarNombreCompleto - Si true, usa nombre completo; si false, mantiene abreviatura
 * @returns {string} - La referencia con el nombre del libro expandido o no
 */
export function expandirReferencia(referencia, usarNombreCompleto = true) {
  if (!referencia || !usarNombreCompleto) return referencia;
  
  // Limpiar la referencia
  const refLimpia = referencia.trim();
  
  // Separar el libro de los capítulos/versículos
  // Buscar el primer número después del nombre del libro
  const match = refLimpia.match(/^((?:\d+\s+)?[A-Za-zÁÉÍÓÚáéíóúñÑ]+(?:\s+[A-Za-z]+)?)\s+(.+)$/);
  
  if (match) {
    const [, libro, capitulos] = match;
    const nombreCompleto = obtenerNombreCompleto(libro);
    return `${nombreCompleto} ${capitulos}`;
  }
  
  // Si no hay capítulos/versículos, solo expandir el libro
  return obtenerNombreCompleto(refLimpia);
}

/**
 * Procesa múltiples referencias separadas por comas
 * @param {string} referencias - Múltiples referencias separadas por comas (ej: "Gn 1, Ex 2, Lv 3")
 * @param {boolean} usarNombreCompleto - Si true, usa nombre completo
 * @returns {string} - Las referencias procesadas
 */
export function expandirReferencias(referencias, usarNombreCompleto = true) {
  if (!referencias) return '';
  
  // Separar por comas y procesar cada referencia
  const refs = referencias.split(',').map(ref => expandirReferencia(ref.trim(), usarNombreCompleto));
  
  return refs.join(', ');
}

/**
 * Obtiene todas las abreviaturas disponibles
 * @returns {string[]} - Array con todas las abreviaturas
 */
export function obtenerTodasLasAbreviaturas() {
  return Object.keys(LIBROS_BIBLIA);
}

/**
 * Obtiene todos los nombres completos disponibles
 * @returns {string[]} - Array con todos los nombres completos
 */
export function obtenerTodosLosNombres() {
  return [...new Set(Object.values(LIBROS_BIBLIA))];
}

/**
 * Busca un libro por nombre parcial (útil para búsquedas)
 * @param {string} busqueda - Texto a buscar
 * @returns {Array} - Array de objetos {abreviatura, nombreCompleto}
 */
export function buscarLibro(busqueda) {
  if (!busqueda) return [];
  
  const termino = busqueda.toLowerCase();
  const resultados = [];
  
  for (const [abrev, nombre] of Object.entries(LIBROS_BIBLIA)) {
    if (
      abrev.toLowerCase().includes(termino) ||
      nombre.toLowerCase().includes(termino)
    ) {
      // Evitar duplicados (por las variantes)
      if (!resultados.find(r => r.nombreCompleto === nombre)) {
        resultados.push({
          abreviatura: abrev,
          nombreCompleto: nombre
        });
      }
    }
  }
  
  return resultados;
}

/**
 * Valida si una abreviatura existe en el mapeo
 * @param {string} abreviatura - La abreviatura a validar
 * @returns {boolean} - true si existe, false si no
 */
export function esAbreviaturaValida(abreviatura) {
  return abreviatura && LIBROS_BIBLIA.hasOwnProperty(abreviatura.trim());
}

/**
 * Extrae el nombre del libro de una referencia completa
 * @param {string} referencia - La referencia completa
 * @returns {string} - Solo el nombre del libro (abreviatura)
 */
export function extraerLibro(referencia) {
  if (!referencia) return '';
  
  const match = referencia.trim().match(/^((?:\d+\s+)?[A-Za-zÁÉÍÓÚáéíóúñÑ]+(?:\s+[A-Za-z]+)?)\s/);
  
  if (match) {
    return match[1].trim();
  }
  
  return referencia.trim();
}

/**
 * Extrae los capítulos/versículos de una referencia completa
 * @param {string} referencia - La referencia completa
 * @returns {string} - Solo los capítulos/versículos
 */
export function extraerCapitulos(referencia) {
  if (!referencia) return '';
  
  const match = referencia.trim().match(/^(?:\d+\s+)?[A-Za-zÁÉÍÓÚáéíóúñÑ]+(?:\s+[A-Za-z]+)?\s+(.+)$/);
  
  if (match) {
    return match[1].trim();
  }
  
  return '';
}

/**
 * Formatea una referencia con opciones personalizadas
 * @param {string} referencia - La referencia a formatear
 * @param {Object} opciones - Opciones de formateo
 * @param {boolean} opciones.nombreCompleto - Si true, usa nombre completo del libro
 * @param {boolean} opciones.mayusculas - Si true, convierte todo a mayúsculas
 * @param {boolean} opciones.negrita - Si true, pone el libro en negrita (HTML)
 * @returns {string} - La referencia formateada
 */
export function formatearReferencia(referencia, opciones = {}) {
  const {
    nombreCompleto = true,
    mayusculas = false,
    negrita = false
  } = opciones;
  
  if (!referencia) return '';
  
  let resultado = expandirReferencia(referencia, nombreCompleto);
  
  if (mayusculas) {
    resultado = resultado.toUpperCase();
  }
  
  if (negrita) {
    const libro = extraerLibro(referencia);
    const libroExpandido = nombreCompleto ? obtenerNombreCompleto(libro) : libro;
    const capitulos = extraerCapitulos(referencia);
    
    if (capitulos) {
      resultado = `<strong>${libroExpandido}</strong> ${capitulos}`;
    } else {
      resultado = `<strong>${libroExpandido}</strong>`;
    }
  }
  
  return resultado;
}

/**
 * Exporta el mapeo completo para uso externo (reportes, etc.)
 * @returns {Object} - El mapeo completo de abreviaturas a nombres
 */
export function exportarMapeo() {
  // Crear un mapeo único (sin variantes duplicadas)
  const mapeoUnico = {};
  const nombresVistos = new Set();
  
  for (const [abrev, nombre] of Object.entries(LIBROS_BIBLIA)) {
    if (!nombresVistos.has(nombre)) {
      mapeoUnico[abrev] = nombre;
      nombresVistos.add(nombre);
    }
  }
  
  return mapeoUnico;
}

// Exportar el mapeo completo para acceso directo si es necesario
export default {
  LIBROS_BIBLIA,
  obtenerNombreCompleto,
  expandirReferencia,
  expandirReferencias,
  obtenerTodasLasAbreviaturas,
  obtenerTodosLosNombres,
  buscarLibro,
  esAbreviaturaValida,
  extraerLibro,
  extraerCapitulos,
  formatearReferencia,
  exportarMapeo
};













